/**
 * Phone sounds: a soft chime when it's your turn, the bell when the host rings
 * it, a low note as each phase begins. iPhones can't vibrate from a web page,
 * so this is how a phone in a pocket gets your attention.
 *
 * Browsers only allow sound after a tap, so the first tap anywhere unlocks it.
 * (An iPhone's silent switch still silences it, as it should.)
 */
let ctx: AudioContext | null = null;

function context(): AudioContext | null {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx ??= new AC();
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    return ctx;
  } catch {
    return null;
  }
}

/** Call once at start-up: the first tap or key press unlocks sound. */
export function armPhoneSound(): void {
  const unlock = () => {
    const c = context();
    if (!c) return;
    // A silent blip actually starts the audio on iOS.
    const o = c.createOscillator();
    const g = c.createGain();
    g.gain.value = 0;
    o.connect(g).connect(c.destination);
    o.start();
    o.stop(c.currentTime + 0.01);
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
}

function tone(c: AudioContext, freq: number, at: number, dur: number, gain: number, type: OscillatorType = "sine"): void {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  o.connect(g).connect(c.destination);
  o.start(at);
  o.stop(at + dur + 0.05);
}

export type PhoneSound = "turn" | "bell" | "phase" | "reveal" | "right" | "wrong" | "lock" | "coin";

export function phoneSound(kind: PhoneSound): void {
  const c = ctx;
  if (!c || c.state !== "running") return;
  const t = c.currentTime + 0.02;
  try {
    switch (kind) {
      case "turn": // your move: two bright notes
        tone(c, 880, t, 0.35, 0.18, "triangle");
        tone(c, 1318.5, t + 0.14, 0.5, 0.15, "triangle");
        break;
      case "bell":
        [[392, 0.2, 2.2], [784, 0.08, 1.4], [988, 0.05, 1]].forEach(([f, g, d]) => tone(c, f!, t, d!, g!));
        break;
      case "phase":
        tone(c, 196, t, 0.9, 0.14, "triangle");
        break;
      case "right": // a bright rising pair
        tone(c, 784, t, 0.25, 0.14, "triangle");
        tone(c, 1174.66, t + 0.1, 0.45, 0.13, "triangle");
        break;
      case "wrong": // a soft falling pair
        tone(c, 330, t, 0.3, 0.12, "triangle");
        tone(c, 247, t + 0.14, 0.5, 0.12, "triangle");
        break;
      case "lock": // a click
        tone(c, 1600, t, 0.04, 0.12, "square");
        break;
      case "coin":
        tone(c, 2637, t, 0.18, 0.08, "triangle");
        tone(c, 3520, t + 0.07, 0.25, 0.06, "triangle");
        break;
      case "reveal": // a low sting
        tone(c, 110, t, 1.6, 0.22, "sawtooth");
        tone(c, 164.8, t + 0.05, 1.4, 0.12, "triangle");
        break;
    }
  } catch {
    /* sound is a nicety */
  }
}
